import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";

// Django auth endpoints (production URL)
const AUTH_BASE_URL = "https://capps-auth-2lg2zbrl7p7b6.ashyhill-1451cba1.swedencentral.azurecontainerapps.io/api/auth";

interface User {
    id: string;
    email: string;
    username: string;
    first_name: string;
    last_name: string;
    oid: string;
    groups: Array<{ id: string; name: string; description: string }>;
    is_active: boolean;
    created_at: string;
}

interface AuthContextType {
    user: User | null;
    accessToken: string | null;
    refreshToken: string | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    login: (email: string, password: string) => Promise<void>;
    register: (email: string, username: string, password: string, passwordConfirm: string, firstName?: string, lastName?: string) => Promise<void>;
    logout: () => Promise<void>;
    refreshAccessToken: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const TOKEN_STORAGE_KEY = "centil_access_token";
const REFRESH_TOKEN_STORAGE_KEY = "centil_refresh_token";
const USER_STORAGE_KEY = "centil_user";

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [accessToken, setAccessToken] = useState<string | null>(null);
    const [refreshToken, setRefreshToken] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    // Load tokens and user from localStorage on mount
    useEffect(() => {
        const storedAccessToken = localStorage.getItem(TOKEN_STORAGE_KEY);
        const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
        const storedUser = localStorage.getItem(USER_STORAGE_KEY);

        if (storedAccessToken && storedRefreshToken && storedUser) {
            setAccessToken(storedAccessToken);
            setRefreshToken(storedRefreshToken);
            setUser(JSON.parse(storedUser));
        }

        setIsLoading(false);
    }, []);

    const login = async (email: string, password: string) => {
        const response = await fetch(`${AUTH_BASE_URL}/login`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({ email, password })
        });

        if (!response.ok) {
            const error = await response.json();
            // Handle different error formats
            let errorMessage = "Login failed";
            if (error.non_field_errors && error.non_field_errors.length > 0) {
                errorMessage = error.non_field_errors[0];
            } else if (error.detail) {
                errorMessage = error.detail;
            }
            throw new Error(errorMessage);
        }

        const data = await response.json();

        // Store tokens and user
        localStorage.setItem(TOKEN_STORAGE_KEY, data.access);
        localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, data.refresh);
        localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(data.user));

        setAccessToken(data.access);
        setRefreshToken(data.refresh);
        setUser(data.user);
    };

    const register = async (
        email: string,
        username: string,
        password: string,
        passwordConfirm: string,
        firstName?: string,
        lastName?: string
    ) => {
        const response = await fetch(`${AUTH_BASE_URL}/register`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({
                email,
                username,
                password,
                password_confirm: passwordConfirm,
                first_name: firstName || "",
                last_name: lastName || ""
            })
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(JSON.stringify(error));
        }

        const data = await response.json();

        // Store tokens and user
        localStorage.setItem(TOKEN_STORAGE_KEY, data.access);
        localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, data.refresh);
        localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(data.user));

        setAccessToken(data.access);
        setRefreshToken(data.refresh);
        setUser(data.user);
    };

    const logout = async () => {
        try {
            if (refreshToken) {
                await fetch(`${AUTH_BASE_URL}/logout`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${accessToken}`
                    },
                    body: JSON.stringify({ refresh: refreshToken })
                });
            }
        } catch (error) {
            console.error("Logout error:", error);
        } finally {
            // Clear local state and storage
            localStorage.removeItem(TOKEN_STORAGE_KEY);
            localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
            localStorage.removeItem(USER_STORAGE_KEY);

            setAccessToken(null);
            setRefreshToken(null);
            setUser(null);
        }
    };

    const refreshAccessToken = async () => {
        if (!refreshToken) {
            throw new Error("No refresh token available");
        }

        const response = await fetch(`${AUTH_BASE_URL}/refresh`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({ refresh: refreshToken })
        });

        if (!response.ok) {
            // Refresh token expired or invalid - logout user
            await logout();
            throw new Error("Token refresh failed");
        }

        const data = await response.json();

        // Update access token
        localStorage.setItem(TOKEN_STORAGE_KEY, data.access);
        setAccessToken(data.access);

        // If new refresh token provided, update it
        if (data.refresh) {
            localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, data.refresh);
            setRefreshToken(data.refresh);
        }
    };

    return (
        <AuthContext.Provider
            value={{
                user,
                accessToken,
                refreshToken,
                isAuthenticated: !!accessToken && !!user,
                isLoading,
                login,
                register,
                logout,
                refreshAccessToken
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};
